import { useId } from "react";
import type { ConsignmentArrangement, ConsignmentBasis } from "@ondo/shared";
import { CONSIGNMENT_ARRANGEMENTS, CONSIGNMENT_BASES } from "@ondo/shared";

import { useModalDialog } from "../lib/useModalDialog.ts";
// THE ENUM'S LABELS BELONG TO THE ENUM, not to either screen that renders it.
// `ondo.consignment_arrangement` and `ondo.consignment_basis` are reused by an
// appointment rather than forked — 20261003096000 decision 10 — and the words
// for their values are reused the same way. A second copy here would read
// identically on the day it was written and drift the first time one of them was
// reworded.
//
// What is NOT borrowed is anything about a consignment's own terms: the labels
// around these two selects are `WORK_AGENT_COPY`'s, and they say "agreed with
// this agent" precisely because an advisor's figures are not a gallery's.
import { CONSIGN_COPY } from "../consign/consign.constants.ts";
import { WORK_AGENT_COPY } from "./work.constants.ts";
import type { AssignAgentView } from "./work.model.ts";
import type { AgentHandlers } from "./useWorkController.ts";

import "../lib/dialog.css";

/**
 * Naming an agent for a work.
 *
 * A native `<dialog>` opened with `showModal()`, the choice `AddHistoryDialog`
 * and `WorkPickerDialog` both make and for the same list of things it does that
 * would otherwise have to be written and got right: modal semantics, Escape, a
 * real focus trap, the rest of the document made INERT rather than merely
 * covered, focus returned to whatever opened it, and the top layer.
 *
 * CLOSING DOES NOT DISCARD, and MOUNTED EVEN WHEN SHUT. Both for the reasons
 * `AddHistoryDialog` states — a stray Escape must not cost what was typed, and
 * it is `close()` rather than unmounting that returns focus to the button.
 *
 * THE CHECK IS A COURTESY, NOT A GATE. On blur it asks the server whether the id
 * can be assigned and shows the answer; Assign stays pressable regardless,
 * because both paths end at `ondo.principal_eligibility()` and cannot disagree.
 * A collector who types an id and presses Enter is not made to wait for a
 * request they never asked for.
 *
 * THERE IS NO NAME SEARCH, and the subtitle says so. `principals_read` is
 * `acts_as(id)`, no endpoint resolves a name to an ONDO id, and none may — so a
 * collector expecting to type "Eleanor" is told once, here, rather than
 * discovering it by typing.
 */
export function AssignAgentDialog({
  view,
  onSetOndoId,
  onSetNote,
  onSetExpiresOn,
  onSetMayDelegate,
  onSetOrganisationOndoId,
  onSetMayConsign,
  onSetTerm,
  onCheck,
  onSubmit,
  onCancel,
  onClose,
}: { view: AssignAgentView } & Omit<
  AgentHandlers,
  "onEnd" | "onRenew" | "onSetScope"
>) {
  const dialog = useModalDialog(view.open);
  const titleId = useId();
  const idField = useId();
  const noteField = useId();
  const termField = useId();
  const termHintId = useId();
  const delegateField = useId();
  const delegateHintId = useId();
  const scopeHeadingId = useId();
  const orgField = useId();
  const orgHintId = useId();
  const consignField = useId();
  const consignHintId = useId();
  const termsHeadingId = useId();
  const arrangementField = useId();
  const basisField = useId();
  const commissionField = useId();
  const feeField = useId();
  const askingField = useId();
  const floorField = useId();
  const currencyField = useId();

  return (
    <dialog
      ref={dialog}
      className="agent"
      aria-labelledby={titleId}
      // Escape and the backdrop both close natively; this is what tells React it
      // happened. Without it the two disagree and the next render calls
      // showModal() on a dialog that is already open.
      onClose={onClose}
    >
      <div className="agent__head">
        <h2 id={titleId} className="agent__title">
          {WORK_AGENT_COPY.dialogTitle}
        </h2>
        <button
          type="button"
          className="agent__shut ondo-dialog__shut"
          aria-label={WORK_AGENT_COPY.close}
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      <p className="agent__subtitle">{WORK_AGENT_COPY.dialogSubtitle}</p>

      <div className="agent__field">
        <label className="agent__label" htmlFor={idField}>
          {WORK_AGENT_COPY.idLabel}
        </label>
        <input
          id={idField}
          className="agent__input"
          value={view.ondoId}
          placeholder={WORK_AGENT_COPY.idPlaceholder}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => onSetOndoId(event.target.value)}
          onBlur={onCheck}
        />
      </div>

      {view.malformed && (
        <p className="agent__problem" role="alert">
          {WORK_AGENT_COPY.malformed}
        </p>
      )}

      {/* The server's own sentence on a refusal — the SAME one the write raises,
          so ignoring the check and pressing Assign does not produce a second
          explanation of one refusal. */}
      {!view.malformed && view.check?.state === "refused" && (
        <p className="agent__problem" role="alert">
          {view.check.message}
        </p>
      )}

      {!view.malformed && view.check?.state === "checking" && (
        <p className="agent__note" role="status">
          {WORK_AGENT_COPY.checking}
        </p>
      )}

      {/* The name appears only once the server has said the id is assignable.
          On the other two verdicts the response carries none, so a refused check
          discloses exactly what a refused write does. */}
      {!view.malformed && view.check?.state === "eligible" && (
        <p className="agent__found" role="status">
          {view.check.displayName}
        </p>
      )}

      <div className="agent__field">
        <label className="agent__label" htmlFor={noteField}>
          {WORK_AGENT_COPY.noteLabel}
        </label>
        <input
          id={noteField}
          className="agent__input"
          value={view.note}
          placeholder={WORK_AGENT_COPY.notePlaceholder}
          onChange={(event) => onSetNote(event.target.value)}
        />
      </div>

      {/* ── THE TERM ──
          Optional, empty by default, and the hint says so. OND-76: "Open-ended
          by default, so a term is something you opt into." A date input with no
          value and that sentence under it is what an opt-in looks like; a
          pre-filled one would be the product agreeing a term on the collector's
          behalf. */}
      <div className="agent__field">
        <label className="agent__label" htmlFor={termField}>
          {WORK_AGENT_COPY.termLabel}
        </label>
        <input
          id={termField}
          type="date"
          className="agent__input"
          value={view.expiresOn}
          aria-describedby={termHintId}
          onChange={(event) => onSetExpiresOn(event.target.value)}
        />
        <p id={termHintId} className="agent__note">
          {WORK_AGENT_COPY.termHint}
        </p>
      </div>

      {/* ── THE DELEGATION PERMISSION ──
          The owner's one choice, unticked. Shown to anybody who may appoint at
          all, which is a weaker gate than `canEnd` and `canRenew` keep — see
          `AssignAgentView.canOfferDelegation` for what cannot be answered yet
          and what closing it would take. A delegate who ticks it is refused in
          the database's own words, which the dialog already prints. */}
      {view.canOfferDelegation && (
        <div className="agent__field agent__field--check">
          <label className="agent__check" htmlFor={delegateField}>
            <input
              id={delegateField}
              type="checkbox"
              checked={view.mayDelegate}
              aria-describedby={delegateHintId}
              onChange={(event) => onSetMayDelegate(event.target.checked)}
            />
            <span>{WORK_AGENT_COPY.delegateLabel}</span>
          </label>
          <p id={delegateHintId} className="agent__note">
            {WORK_AGENT_COPY.delegateHint}
          </p>
        </div>
      )}

      {/* ── THE SCOPE: WHO IT REACHES, AND WHAT IT PERMITS ──

          OND-83 and OND-82, in one fieldset because they are one question the
          owner is answering: how much of my picture does this appointment hand
          over. The heading says both halves.

          IT SITS ABOVE THE AGREED TERMS DELIBERATELY. The terms are a record of
          what was agreed; this is a grant of what may be done. A collector
          skimming the form should meet the permission before the paperwork, and
          the two must not read as one section — 20261003099000 decision 6
          rejects inferring either from the other for the same reason. */}
      <fieldset className="agent__terms" aria-labelledby={scopeHeadingId}>
        <legend id={scopeHeadingId} className="agent__label">
          {WORK_AGENT_COPY.scopeHeading}
        </legend>

        <div className="agent__field">
          <label className="agent__label" htmlFor={orgField}>
            {WORK_AGENT_COPY.organisationLabel}
          </label>
          <input
            id={orgField}
            className="agent__input"
            value={view.organisationOndoId}
            placeholder={WORK_AGENT_COPY.idPlaceholder}
            autoComplete="off"
            spellCheck={false}
            aria-describedby={orgHintId}
            onChange={(event) => onSetOrganisationOndoId(event.target.value)}
          />
          <p id={orgHintId} className="agent__note">
            {WORK_AGENT_COPY.organisationHint}
          </p>
        </div>

        {/* Shape only, exactly as the agent's own id is. Whether the id exists,
            is an organisation, or is the agent just named is ONE refusal in
            SQL, and it arrives as the server's own sentence. */}
        {view.malformedOrganisation && (
          <p className="agent__problem" role="alert">
            {WORK_AGENT_COPY.malformed}
          </p>
        )}

        {/* ── THE CONSIGNMENT PERMISSION ──
            Unticked. OND-82: "Appointment alone must not automatically grant
            this power." No `canOffer…` gate, unlike the delegation box: a
            delegator who does not hold it is refused by name in the database's
            own words, which this dialog already prints. */}
        <div className="agent__field agent__field--check">
          <label className="agent__check" htmlFor={consignField}>
            <input
              id={consignField}
              type="checkbox"
              checked={view.mayConsign}
              aria-describedby={consignHintId}
              onChange={(event) => onSetMayConsign(event.target.checked)}
            />
            <span>{WORK_AGENT_COPY.consignLabel}</span>
          </label>
          <p id={consignHintId} className="agent__note">
            {WORK_AGENT_COPY.consignHint}
          </p>
        </div>
      </fieldset>

      {/* ── THE AGREED TERMS ──
          A section of its own, headed "agreed with this agent" rather than
          "Terms", for OND-43's reason: a work may be on consignment to a gallery
          at one floor while an advisor is appointed over it at another, and a
          form that said only "Floor" would be the first place a dealer read the
          wrong number as binding. */}
      <fieldset className="agent__terms" aria-labelledby={termsHeadingId}>
        <legend id={termsHeadingId} className="agent__label">
          {WORK_AGENT_COPY.termsHeading}
        </legend>

        <div className="agent__row">
          <div className="agent__field">
            <label className="agent__label" htmlFor={arrangementField}>
              {WORK_AGENT_COPY.arrangementLabel}
            </label>
            <select
              id={arrangementField}
              className="agent__input"
              value={view.terms.arrangement}
              onChange={(event) =>
                onSetTerm("arrangement", event.target.value as ConsignmentArrangement | "")
              }
            >
              <option value="">{WORK_AGENT_COPY.unset}</option>
              {CONSIGNMENT_ARRANGEMENTS.map((value) => (
                <option key={value} value={value}>
                  {CONSIGN_COPY.arrangement[value]}
                </option>
              ))}
            </select>
          </div>

          <div className="agent__field">
            <label className="agent__label" htmlFor={basisField}>
              {WORK_AGENT_COPY.basisLabel}
            </label>
            <select
              id={basisField}
              className="agent__input"
              value={view.terms.basis}
              onChange={(event) =>
                onSetTerm("basis", event.target.value as ConsignmentBasis | "")
              }
            >
              <option value="">{WORK_AGENT_COPY.unset}</option>
              {CONSIGNMENT_BASES.map((value) => (
                <option key={value} value={value}>
                  {CONSIGN_COPY.basis[value]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* COMMISSION OR A FEE, never both — `agentTermsProblem` says so before
            the check constraint has to. */}
        <div className="agent__row">
          <div className="agent__field">
            <label className="agent__label" htmlFor={commissionField}>
              {WORK_AGENT_COPY.commissionLabel}
            </label>
            <input
              id={commissionField}
              className="agent__input"
              inputMode="decimal"
              value={view.terms.commission}
              onChange={(event) => onSetTerm("commission", event.target.value)}
            />
          </div>
          <div className="agent__field">
            <label className="agent__label" htmlFor={feeField}>
              {WORK_AGENT_COPY.feeLabel}
            </label>
            <input
              id={feeField}
              className="agent__input"
              inputMode="decimal"
              value={view.terms.fee}
              onChange={(event) => onSetTerm("fee", event.target.value)}
            />
          </div>
        </div>

        <div className="agent__row">
          <div className="agent__field">
            <label className="agent__label" htmlFor={askingField}>
              {WORK_AGENT_COPY.askingLabel}
            </label>
            <input
              id={askingField}
              className="agent__input"
              inputMode="decimal"
              value={view.terms.agreedAsking}
              onChange={(event) => onSetTerm("agreedAsking", event.target.value)}
            />
          </div>
          <div className="agent__field">
            <label className="agent__label" htmlFor={floorField}>
              {WORK_AGENT_COPY.floorLabel}
            </label>
            <input
              id={floorField}
              className="agent__input"
              inputMode="decimal"
              value={view.terms.agreedFloor}
              onChange={(event) => onSetTerm("agreedFloor", event.target.value)}
            />
          </div>
          <div className="agent__field">
            <label className="agent__label" htmlFor={currencyField}>
              {WORK_AGENT_COPY.currencyLabel}
            </label>
            <input
              id={currencyField}
              className="agent__input"
              value={view.terms.currency}
              maxLength={3}
              spellCheck={false}
              onChange={(event) => onSetTerm("currency", event.target.value)}
            />
          </div>
        </div>
      </fieldset>

      {/* The two refusals the form can make for itself. Everything else is the
          database's, and arrives as the server's own sentence. */}
      {view.problem !== null && (
        <p className="agent__problem" role="alert">
          {view.problem}
        </p>
      )}

      <div className="agent__actions">
        {/* Cancel is the only control that discards. See the header. */}
        <button type="button" className="agent__cancel" onClick={onCancel}>
          {WORK_AGENT_COPY.cancel}
        </button>
        <button
          type="button"
          className="agent__submit"
          disabled={!view.canSubmit}
          onClick={onSubmit}
        >
          {view.busy ? WORK_AGENT_COPY.submitting : WORK_AGENT_COPY.submit}
        </button>
      </div>
    </dialog>
  );
}
